/**
 * Evaluation History Page
 *
 * Shows employee's evaluation history with detailed scores and trends.
 */

import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useAuth } from '../auth';
import { useEvaluations, useEvaluation } from '../evaluations/useEvaluations';
import { ScoreDisplay } from '../evaluations/wizard/ScoreInput';
import { useState } from 'react';

export function EvaluationHistoryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: evaluations, isLoading } = useEvaluations();
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const employeeId = user?.employeeId;

  // Filter completed evaluations for this employee
  const myEvaluations = evaluations?.data
    ?.filter((e) => e.employeeId === employeeId && e.status === 'final')
    .sort((a, b) => new Date(b.periodeEnd).getTime() - new Date(a.periodeEnd).getTime()) || [];

  // Calculate statistics
  const scores = myEvaluations.map((e) => parseFloat(e.totalScore || '0'));
  const avgScore = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
  const maxScore = scores.length > 0 ? Math.max(...scores) : 0;
  const minScore = scores.length > 0 ? Math.min(...scores) : 0;
  const firstScore = scores[0] ?? 0;
  const lastScore = scores[scores.length - 1] ?? 0;
  const trend = scores.length >= 2 ? firstScore - lastScore : 0;

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Laden...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="evaluation-history-page" className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mb-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Terug naar dashboard
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Beoordelingsgeschiedenis</h1>
          <p className="mt-1 text-sm text-gray-500">
            Bekijk al uw afgeronde beoordelingen en uw groei over tijd.
          </p>
        </div>

        {myEvaluations.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h3 className="mt-4 text-sm font-medium text-gray-900">Geen beoordelingen</h3>
            <p className="mt-2 text-sm text-gray-500">
              U heeft nog geen afgeronde beoordelingen.
            </p>
          </div>
        ) : (
          <>
            {/* Statistics Overview */}
            <div className="mb-6 grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-white p-4 rounded-lg border border-gray-200 text-center">
                <p className="text-2xl font-bold text-gray-900">{myEvaluations.length}</p>
                <p className="text-sm text-gray-500">Beoordelingen</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
                <p className="text-2xl font-bold text-green-700">{avgScore.toFixed(2)}</p>
                <p className="text-sm text-green-600">Gemiddeld</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
                <p className="text-2xl font-bold text-blue-700">{maxScore.toFixed(2)}</p>
                <p className="text-sm text-blue-600">Hoogste</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-center">
                <p className="text-2xl font-bold text-gray-700">{minScore.toFixed(2)}</p>
                <p className="text-sm text-gray-500">Laagste</p>
              </div>
              <div className={`p-4 rounded-lg border text-center ${
                trend > 0 ? 'bg-green-50 border-green-200' :
                trend < 0 ? 'bg-red-50 border-red-200' :
                'bg-gray-50 border-gray-200'
              }`}>
                <p className={`text-2xl font-bold ${
                  trend > 0 ? 'text-green-700' :
                  trend < 0 ? 'text-red-700' :
                  'text-gray-700'
                }`}>
                  {trend > 0 ? '+' : ''}{trend.toFixed(2)}
                </p>
                <p className={`text-sm ${
                  trend > 0 ? 'text-green-600' :
                  trend < 0 ? 'text-red-600' :
                  'text-gray-500'
                }`}>Groei</p>
              </div>
            </div>

            {/* Score Chart */}
            <div className="mb-6 bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Score Verloop</h3>
              <div className="flex items-end justify-around gap-4 h-48">
                {myEvaluations.slice().reverse().map((evaluation, index) => {
                  const score = parseFloat(evaluation.totalScore || '0');
                  const height = Math.max(10, (score / 5) * 100);
                  const isSelected = selectedId === evaluation.id;

                  return (
                    <button
                      key={evaluation.id}
                      onClick={() => setSelectedId(isSelected ? null : evaluation.id)}
                      className="flex flex-col items-center group"
                    >
                      <span className={`text-xs font-medium mb-1 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`}>
                        {score.toFixed(1)}
                      </span>
                      <div
                        className={`w-12 rounded-t transition-all ${
                          isSelected ? 'bg-blue-600' :
                          index === myEvaluations.length - 1 ? 'bg-green-500' :
                          'bg-blue-300 group-hover:bg-blue-400'
                        }`}
                        style={{ height: `${height}%` }}
                      ></div>
                      <span className="text-xs text-gray-400 mt-2">
                        {new Date(evaluation.periodeEnd).toLocaleDateString('nl-NL', { month: 'short', year: '2-digit' })}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Evaluation Details */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Alle Beoordelingen</h3>
              </div>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Periode</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Taken (70)</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Comp. (30)</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Totaal</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Afgerond</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Print</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {myEvaluations.map((evaluation) => (
                    <tr
                      key={evaluation.id}
                      className={`hover:bg-gray-50 cursor-pointer ${selectedId === evaluation.id ? 'bg-blue-50' : ''}`}
                      onClick={() => setSelectedId(selectedId === evaluation.id ? null : evaluation.id)}
                    >
                      <td className="px-6 py-4">
                        <p className="text-sm font-medium text-gray-900">
                          {new Date(evaluation.periodeStart).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })}
                        </p>
                        <p className="text-xs text-gray-500">
                          t/m {new Date(evaluation.periodeEnd).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })}
                        </p>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {evaluation.taskScore ? (
                          <ScoreDisplay score={Math.round(parseFloat(evaluation.taskScore))} size="sm" showLabel={false} />
                        ) : '—'}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {evaluation.competencyScore ? (
                          <ScoreDisplay score={Math.round(parseFloat(evaluation.competencyScore))} size="sm" showLabel={false} />
                        ) : '—'}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {evaluation.totalScore ? (
                          <span className="text-lg font-bold text-green-700">
                            {parseFloat(evaluation.totalScore).toFixed(2)}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-gray-500">
                        {evaluation.finalizedAt
                          ? new Date(evaluation.finalizedAt).toLocaleDateString('nl-NL')
                          : '—'}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`/beoordeling/${evaluation.id}/print`, '_blank');
                          }}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Scoringsformulier afdrukken"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Selected Evaluation Detail */}
            {selectedId && <EvaluationDetailPanel evaluationId={selectedId} />}
          </>
        )}
      </div>
    </Layout>
  );
}

function EvaluationDetailPanel({ evaluationId }: { evaluationId: number }) {
  const { data: evaluation, isLoading } = useEvaluation(evaluationId);

  if (isLoading) {
    return (
      <div className="mt-6 bg-white rounded-lg border border-gray-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/4"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  if (!evaluation) return null;

  const taskScores = evaluation.taskScores || [];
  const competencyScores = evaluation.competencyScores || [];
  const agreements = evaluation.agreements || [];

  return (
    <div className="mt-6 bg-white rounded-lg border border-blue-200 shadow-sm overflow-hidden">
      <div className="px-6 py-4 bg-blue-50 border-b border-blue-200">
        <h3 className="text-lg font-medium text-blue-900">Details Beoordeling</h3>
        <p className="text-sm text-blue-700">
          {new Date(evaluation.periodeStart).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })} -{' '}
          {new Date(evaluation.periodeEnd).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' })}
        </p>
      </div>

      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Task Scores */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Taakscores</h4>
          {taskScores.length > 0 ? (
            <div className="space-y-2">
              {taskScores.map((ts) => (
                <div key={ts.taskId} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm text-gray-600">Taak #{ts.taskId}</span>
                  <ScoreDisplay score={ts.score} size="sm" showLabel={false} />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">Geen taakscores</p>
          )}
        </div>

        {/* Competency Scores */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Competentiescores</h4>
          {competencyScores.length > 0 ? (
            <div className="space-y-2">
              {competencyScores.map((cs) => (
                <div key={cs.competencyId} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm text-gray-600">Competentie #{cs.competencyId}</span>
                  <ScoreDisplay score={cs.score} size="sm" showLabel={false} />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">Geen competentiescores</p>
          )}
        </div>
      </div>

      {/* Agreements */}
      {agreements.length > 0 && (
        <div className="px-6 pb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Afspraken</h4>
          <div className="space-y-2">
            {agreements.map((agreement) => (
              <div key={agreement.id} className="flex items-start gap-2 p-3 bg-gray-50 rounded">
                <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                  agreement.isCompleted ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {agreement.isCompleted ? 'Afgerond' : agreement.type}
                </span>
                <p className="text-sm text-gray-700">{agreement.beschrijving}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
